'use client'

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'

export function LiveChat() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Array<{ id: string; text: string; sender: 'user' | 'agent' }>>([
    {
      id: '1',
      text: 'Welcome to Rombex! How can we help you today?',
      sender: 'agent',
    },
  ])
  const [input, setInput] = useState('')

  const handleSend = () => {
    if (!input.trim()) return

    const newMessage = {
      id: Date.now().toString(),
      text: input,
      sender: 'user' as const,
    }

    setMessages((prev) => [...prev, newMessage])
    setInput('')

    // Simulate agent response after 1 second
    setTimeout(() => {
      const responses = [
        'Thanks for your message! Our team will respond shortly.',
        'We appreciate your interest in Rombex!',
        'Please let us know more about your needs.',
        'Feel free to contact us at info@rombex.com for detailed support.',
      ]
      const randomResponse = responses[Math.floor(Math.random() * responses.length)]
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          text: randomResponse,
          sender: 'agent',
        },
      ])
    }, 1000)
  }

  return (
    <>
      {/* Chat Widget */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 z-40 w-96 max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-background shadow-2xl sm:bottom-6 sm:right-6"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border bg-gradient-to-r from-brand/5 to-brand/10 p-4">
              <div>
                <h3 className="font-semibold text-foreground">Chat with us</h3>
                <p className="text-xs text-muted-foreground">Usually responds within minutes</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Close chat"
              >
                <X className="size-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-80 overflow-y-auto space-y-4 p-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs rounded-lg px-4 py-2 text-sm ${
                      message.sender === 'user'
                        ? 'bg-brand text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="border-t border-border p-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Type your message..."
                  className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
                <Button
                  onClick={handleSend}
                  size="icon"
                  className="bg-brand hover:bg-brand/90"
                  aria-label="Send message"
                >
                  <Send className="size-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-40 flex size-14 items-center justify-center rounded-full bg-brand text-white shadow-lg hover:shadow-xl transition-shadow"
        aria-label="Open chat"
      >
        {isOpen ? <X className="size-6" /> : <MessageCircle className="size-6" />}
      </motion.button>
    </>
  )
}
